import { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';
import { PerformanceMetric } from '../../../constants/telemetry';
import { ListEnvironmentsResponse } from '../../../data/services/data-plane-api/environment';
import { ClientError, FailureOperation, isAggregatedFailure, isAggregatedSuccess } from '../../../models/common';
import { combineResults, getErrorCodes } from '../../../utilities/aggregated-result';
import { aggregateFailureResponses } from '../../../utilities/failure';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import {
    discoverEnvironmentsInTenant,
    discoverEnvironmentsInTenantError,
    discoverEnvironmentsInTenantFailed,
    discoverEnvironmentsInTenantSuccess,
    listEnvironments,
} from '../../actions/environment/environment-action-creators';
import { DiscoverEnvironmentsInTenantAction } from '../../actions/environment/environment-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { getIdsForProjectsRequiringEnvironments } from './selectors';

export function* discoverEnvironmentsInTenantSaga(action: DiscoverEnvironmentsInTenantAction): SagaIterator {
    const startTime = new Date();

    try {
        const ids: string[] = yield select(getIdsForProjectsRequiringEnvironments);
        const responses: ListEnvironmentsResponse[] = yield all(ids.map((id) => putAndAwait(listEnvironments({ id }))));
        const completeResult = combineResults(responses);

        if (isAggregatedFailure(completeResult)) {
            const { failures } = completeResult;
            const failure = aggregateFailureResponses(FailureOperation.DiscoverEnvironmentsInTenant, ...failures);
            yield put(discoverEnvironmentsInTenantFailed({ failure }));
        } else {
            yield put(discoverEnvironmentsInTenantSuccess());
        }

        yield resolveAction(action, completeResult);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.DiscoverEnvironmentsInTenant,
            startTime,
            completeResult.outcome,
            isAggregatedSuccess(completeResult) ? undefined : { errorCodes: getErrorCodes(completeResult) }
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.DiscoverEnvironmentsInTenant);
        yield put(discoverEnvironmentsInTenantError({ error }));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.DiscoverEnvironmentsInTenant,
            startTime,
            AsyncOutcome.Error,
            { errorCodes: [error.code] }
        );
    }
}

export function* discoverEnvironmentsInTenantListenerSaga(): SagaIterator {
    yield takeLeading(discoverEnvironmentsInTenant, discoverEnvironmentsInTenantSaga);
}
